import { debounce, isEmpty } from "lodash";
import { Link, Package, Trash2 } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";

import { InputNumber } from "../../../../../components/InputText/InputNumber";
import { Badge } from "../../../../../components/ui/common/badge";
import { Button } from "../../../../../components/ui/common/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../../components/ui/common/card";
import { getDisplayText, isCheckPercentageIrrigation } from "../../../../../utils/utils";
import { LinkedProductForm } from "../../../types/LinkProductTypes";

export default function MatchingTable({
  currentMainProduct,
  removeLinkedProduct,
  disableEdit,
  patchLinkVolume,
  refetchProductAverage
}: any) {
  const checkLinkProductIsEmpty = isEmpty(currentMainProduct);
  const {
    control,
    formState: { errors },
    trigger,
    reset
  } = useForm<LinkedProductForm>({
    mode: "all",
    defaultValues: {
      products: currentMainProduct?.map((p: any) => ({
        ...p,
        volume: p.volume ?? null
      }))
    }
  });

  const { fields } = useFieldArray({
    control,
    name: "products"
  });

  const debouncedPatch = useMemo(
    () =>
      debounce((val: any) => {
        patchLinkVolume(val, {
          onSuccess() {
            refetchProductAverage();
          }
        });
      }, 500),
    [patchLinkVolume]
  );

  useEffect(() => {
    if (currentMainProduct) {
      reset({
        products: currentMainProduct.map((p: any) => ({
          ...p,
          volume: p.volume ?? null
        }))
      });
    }
  }, [currentMainProduct, reset]);

  const handleUploadVolume = (index: any, linkedProduct: any, e: any) => {
    trigger(`products.${index}.volume`).then(valid => {
      if (valid) {
        debouncedPatch({
          averageProductId: linkedProduct?.productId,
          salesUnitId: linkedProduct?.salesUnitId,
          volumePercentage: e?.target?.value | 0
        });
      }
    });
  };

  return (
    <Card className="tw-p-0 tw-border tw-rounded-tl-lg tw-rounded-tr-lg">
      <CardHeader className="tw-bg-main-table-background tw-text-white tw-border tw-rounded-tl-lg tw-rounded-tr-lg tw-p-0">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-space-x-3">
            <div className="tw-w-10 tw-h-10 tw-text-main-table-header-color tw-rounded-lg tw-flex tw-items-center tw-justify-center">
              <Link className="tw-w-5 tw-h-5 tw-text-main-table-header-color" />
            </div>
            <div>
              <CardTitle className="tw-text-xl tw-text-main-table-header-color tw-font-semibold">
                Linked Products
              </CardTitle>
              <p className="tw-text-main-table-header-color tw-text-sm tw-mt-0">
                {currentMainProduct?.name}
              </p>
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-space-x-3 tw-p-1">
            <Badge
              variant="secondary"
              className="tw-bg-white/20 tw-text-main-table-header-color tw-border-white/30 hover:tw-bg-white/30 tw-p-1"
            >
              {`${currentMainProduct?.length || 0} products`}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="tw-p-0">
        {checkLinkProductIsEmpty ? (
          <div className="tw-p-8 tw-text-center tw-text-gray-500 tw-mt-0 tw-pt-0">
            <Package className="tw-w-12 tw-h-12 tw-mx-auto tw-mb-1 tw-text-gray-300 tw-text-main-color" />
            <p>No products linked yet. Select a sub-company and add products above.</p>
          </div>
        ) : (
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full">
              <thead className="tw-bg-gradient-to-r tw-from-green-50 tw-to-green-100 tw-border-green-200 tw-mb-0">
                <tr>
                  <th className="tw-text-left tw-p-1 tw-text-main-color tw-font-semibold">
                    <div className="tw-flex tw-items-center tw-space-x-2">
                      <span className="tw-text-main-color">Sub company</span>
                    </div>
                  </th>
                  <th className="tw-text-left tw-p-1 tw-text-main-color tw-font-semibold">
                    <div className="tw-flex tw-items-center tw-space-x-2">
                      <span className="tw-text-main-color">Product</span>
                    </div>
                  </th>
                  <th className="tw-text-left tw-p-1 tw-text-main-color tw-font-semibold">
                    <div className="tw-flex tw-items-center tw-space-x-2">
                      <span className="tw-text-main-color">Sales Unit</span>
                    </div>
                  </th>
                  <th className="tw-text-left tw-p-1 tw-text-main-color tw-font-semibold">
                    <div className="tw-flex tw-items-center tw-space-x-2">
                      <span className="tw-text-main-color">Climate Impact Change(kgCO2eq/kg)</span>
                    </div>
                  </th>
                  <th className="tw-text-left tw-p-1 tw-text-main-color tw-font-semibold">Product volume %</th>
                  {!disableEdit ? (
                    <th className="tw-text-left tw-p-1 tw-text-main-color tw-font-semibold tw-text-center">
                      Actions
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {fields?.map((linkedProduct: any, index: any) => (
                  <tr
                    key={linkedProduct._id}
                    className={
                      index % 2 === 0
                        ? "tw-bg-white hover:tw-bg-green-50/50"
                        : "tw-bg-gray-50 hover:tw-bg-green-50"
                    }
                  >
                    <td
                      className={`p-1 font-medium ${
                        getDisplayText(linkedProduct?.subCompanyName).className
                      }`}
                    >
                      {linkedProduct?.subCompanyName}
                    </td>
                    <td
                      className={`p-1 font-medium ${
                        getDisplayText(linkedProduct?.productName).className
                      }`}
                    >
                      {linkedProduct?.productName || "Product name is missing"}
                    </td>
                    <td
                      className={`p-1 font-medium ${
                        getDisplayText(linkedProduct?.salesUnitName).className
                      }`}
                    >
                      {linkedProduct?.salesUnitName || "Sales unit name is missing"}
                    </td>
                    <td
                      className={`p-1 font-medium ${
                        getDisplayText(linkedProduct?.totalImpactClimateChangeMeasure).className
                      }`}
                    >
                      {linkedProduct?.totalImpactClimateChangeMeasure ||
                        "Climate impact name is missing"}
                    </td>
                    <td
                      className={`p-1 font-medium ${
                        getDisplayText(linkedProduct?.volume).className
                      }`}
                    >
                      <InputNumber
                        control={control}
                        placeholder="Product volume %"
                        name={`products.${index}.volume`}
                        errors={errors?.products?.[index]?.volume}
                        onChangeFunction={(e: any) => handleUploadVolume(index, linkedProduct, e)}
                        otherValidation={{
                          validate: {
                            valueCheck: (v: any) =>
                              isCheckPercentageIrrigation(v) || "Volume is not valid"
                          }
                        }}
                      />
                    </td>
                    {!disableEdit ? (
                      <td className="tw-p-1 tw-text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLinkedProduct(linkedProduct.salesUnitId)}
                          className="tw-text-red-600 hover:tw-text-green-800 hover:tw-text-green-800"
                          type="button"
                        >
                          <Trash2 className="tw-w-4 tw-h-4 tw-text-green-800" />
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
